import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';

const FIELDS = ['title', 'event_type', 'event_date', 'location_label', 'guest_count', 'budget'];

// event_date arrives as a flexible string ("2027-08-15", or an ISO timestamp,
// or even free-form). The native <input type="date"> only accepts yyyy-mm-dd
// so we coerce as best we can and gracefully fall back to empty when we
// can't parse it.
const toDateInput = (raw) => {
  if (!raw) return '';
  if (typeof raw !== 'string') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch (_) {
    return '';
  }
};

const buildInitial = (ev) => ({
  // B-EXP-6.21 — canonical customer-facing event title, edited
  // alongside the rest of Event Details.
  title: ev?.title || '',
  event_type: ev?.event_type || '',
  event_date: toDateInput(ev?.event_date),
  location_label: ev?.location_label || '',
  guest_count: ev?.guest_count != null ? String(ev.guest_count) : '',
  guests_raw: ev?.guest_count,
  budget: ev?.budget != null ? String(ev.budget) : '',
  budget_raw: ev?.budget,
});

export default function EditEventModal({ open, onClose, event, onSave }) {
  const initial = useMemo(() => buildInitial(event), [event]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(initial); }, [initial, open]);

  // Diff with type-coercion for number fields. Empty string === "no change".
  const changes = useMemo(() => {
    const out = {};
    FIELDS.forEach((k) => {
      const baseRaw = initial[k] || '';
      const next = form[k] || '';
      if (baseRaw === next) return;
      if (k === 'guest_count' || k === 'budget') {
        if (next === '') return; // don't blank out — service treats as no-op anyway
        const n = Number(next);
        if (Number.isNaN(n)) return;
        out[k] = n;
      } else {
        out[k] = next;
      }
    });
    return out;
  }, [form, initial]);

  const isDirty = Object.keys(changes).length > 0;

  const submit = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    try {
      await onSave(changes);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent data-testid="edit-event-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit event details</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Saved as a Concierge working copy. The customer&apos;s original intake is not changed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="ee-title" className="text-xs text-gray-600">Event title</Label>
            <Input
              id="ee-title"
              data-testid="edit-event-title-input"
              placeholder="e.g. Disco Birthday Party"
              value={form.title}
              maxLength={120}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Shown to the customer on Event Home and here on the case
              workspace. Falls back to the event type if left blank.
            </p>
          </div>
          <div>
            <Label htmlFor="ee-type" className="text-xs text-gray-600">Event type</Label>
            <Input
              id="ee-type"
              data-testid="edit-event-type-input"
              value={form.event_type}
              onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="ee-date" className="text-xs text-gray-600">Event date</Label>
            <Input
              id="ee-date"
              type="date"
              data-testid="edit-event-date-input"
              value={form.event_date}
              onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="ee-location" className="text-xs text-gray-600">Location</Label>
            <Input
              id="ee-location"
              data-testid="edit-event-location-input"
              value={form.location_label}
              onChange={(e) => setForm((f) => ({ ...f, location_label: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ee-guests" className="text-xs text-gray-600">Guests</Label>
              <Input
                id="ee-guests"
                type="number"
                min="0"
                data-testid="edit-event-guests-input"
                value={form.guest_count}
                onChange={(e) => setForm((f) => ({ ...f, guest_count: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="ee-budget" className="text-xs text-gray-600">Budget</Label>
              <Input
                id="ee-budget"
                type="number"
                min="0"
                step="0.01"
                data-testid="edit-event-budget-input"
                value={form.budget}
                onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="edit-event-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!isDirty || saving}
            data-testid="edit-event-save-btn"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
